import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import ClientStepNavigator from '../stepNavigation/ClientStepNavigator.tsx';
import ClientDocumentsSection from './components/ClientDocumentsSection.tsx';
import GeneratedDocumentSection from './components/GeneratedDocumentSection.tsx';
import { CLIENT_STEP2_TEXT } from './constants.ts';
import { useClientStep2Documents } from './helpers/useClientStep2Documents.ts';
import { useClientStep2Page } from './helpers/useClientStep2Page.ts';
import { useClientStep2PdfPreview } from './helpers/useClientStep2PdfPreview.ts';
import { useClientStep2Signing } from './helpers/useClientStep2Signing.ts';
import { getClientStep2SigningDocumentPreviewFileId } from './utils.ts';

const ClientStep2Page: React.FC = () => {
  const {
    applicationError,
    contragentId,
    effectiveApplicationId,
    headerProps,
    isApplicationLoading,
    isStepActionsDisabled,
    modalAlert,
    modalSuccess,
    redirectPath,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    onModalClose,
    onSuccessModalClose,
  } = useClientStep2Page();
  const documentSectionProps = useClientStep2Documents({
    applicationId: effectiveApplicationId,
    contragentId,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const hasRequiredFoundingDocument = documentSectionProps.documents.length > 0;
  const { pdfUrl, isDocumentLoading, handleOpenPdf, handlePreviewDocument } =
    useClientStep2PdfPreview({
      generatedDocument: documentSectionProps.generatedDocument,
      hasApplicationFilesLoaded: documentSectionProps.hasApplicationFilesLoaded,
      isApplicationFilesLoading: documentSectionProps.isApplicationFilesLoading,
      setModalAlert,
      showGuaranteesRequestError,
    });
  const {
    deepLink,
    documents: signingDocuments,
    isSignatureModalOpen,
    isSuccessRedirectLocked,
    isSigningLoading,
    timerRef,
    handleSign,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleTimeUp,
    onIITSign,
  } = useClientStep2Signing({
    applicationId: effectiveApplicationId,
    applicationFiles: documentSectionProps.applicationFiles,
    contragentId,
    hasRequiredFoundingDocument,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
  });
  const handleSigningDocumentPreview = React.useCallback(
    (documentId: number | string) => {
      const fileId = getClientStep2SigningDocumentPreviewFileId(signingDocuments, documentId);

      void handlePreviewDocument(fileId ?? '');
    },
    [handlePreviewDocument, signingDocuments]
  );

  useFreshGuaranteesRequestError(applicationError, showGuaranteesRequestError);

  if (redirectPath && !modalSuccess.text && !isSuccessRedirectLocked) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={onModalClose}
      onSuccessModalClose={onSuccessModalClose}
    >
      {isSigningLoading && <FullPageLoader />}
      <ElectronicSignature
        isModalOpen={isSignatureModalOpen}
        handleCloseModal={handleCloseSignatureModal}
        handleSignatureDiia={handleDiiaSignature}
        handleSignatureIIT={handleIITSignature}
        deepLink={deepLink}
        onIITSign={onIITSign}
        handleTimeUp={handleTimeUp}
        handlePreview={handleSigningDocumentPreview}
        ref={timerRef}
        documents={signingDocuments}
        waitForDiiaInitialization
      />
      <ApplicationHeader {...headerProps} />

      {isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GeneratedDocumentSection
            isLoading={isDocumentLoading}
            pdfUrl={pdfUrl}
            isDisabled={isStepActionsDisabled}
            onOpenPdf={handleOpenPdf}
          />

          <ClientDocumentsSection {...documentSectionProps} isDisabled={isStepActionsDisabled} />

          <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
            <Button
              variant="contained"
              color="primary"
              type="button"
              sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
              disabled={isStepActionsDisabled}
              onClick={handleSign}
            >
              {CLIENT_STEP2_TEXT.signButton}
            </Button>
          </Box>

          <ClientStepNavigator
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="step2"
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ClientStep2Page;
